/**
 * UI object to bind and manage the tab visibility, paging, and buttons in the config ui.
 *
 * @property {HTMLCollectionOf<HTMLElement>} content elements tagged with a CSS class that indicates it is content.
 * @property {Object} buttons obj containing references to the links and direction buttons.
 * @property {number} active_idx id of the tab that is active and visible.
 * @property {boolean} check_validity flag that indicates if a form needs to be checked for validity.
 * @property {function|null} callback to use on the button's clicked methods (optional).
 */

type ToggleContentCallback = (element: HTMLElement, idx: number) => void | null;
type ToggleContentButtons = { 
    links: HTMLCollectionOf<Element>,
    mobile_links: HTMLCollectionOf<Element>,
    direction_buttons?: HTMLCollectionOf<Element>
};

export class ToggleContent {
    content: HTMLCollectionOf<HTMLElement>;
    buttons: ToggleContentButtons;
    active_idx: number;
    check_validity: boolean;
    callback: ToggleContentCallback;

    /**
     * Accepts the html elements to bind the controls, then sets the active (visible) tab.
     *
     * @example:
     * content = document.getElementsByClassName('js-tab-content');
     * buttons = {'direction_buttons': HTMLCollectionof<Element>, 'links': HTMLCollectionOf<Element>}
     *
     * @param {HTMLCollectionOf<HTMLElement>} content elements tagged with a CSS class that indicates it is content.
     * @param {Object} buttons obj containing references to the links and direction buttons.
     * @param {number} active_idx id of the tab that is active and visible.
     * @param {boolean} check_validity flag that indicates if a form needs to be checked for validity.
     * @param {Function} callback Optional callback for when a tab is clicked.
     */
    constructor(
        content: HTMLCollectionOf<HTMLElement>,
        buttons : ToggleContentButtons,
        active_idx: number,
        check_validity: boolean = false,
        callback: ToggleContentCallback = null
    ) {
        this.content = content;
        this.buttons = buttons;
        this.active_idx = 0;
        this.check_validity = check_validity;
        this.callback = callback;

        this.bind();
        this.set_active(active_idx);
    }

    /**
     * Bind the buttons to a click event that handles which content section to display.
     *
     * @returns {void}
     */
    bind(): void {
        for (const link of this.buttons.links) {
            link.addEventListener('click', this.link_clicked.bind(this), false);
        }

        for (const link of this.buttons.mobile_links) {
            link.addEventListener('click', this.link_clicked.bind(this), false);
        }

        if (this.buttons.direction_buttons) {
            for (const direction_button of this.buttons.direction_buttons) {
                direction_button.addEventListener('click', this.direction_button_clicked.bind(this), false);
            }
        }
    }

    /**
     * Handles the selected link to display the corresponding content section.
     *
     * @param {Event} e The element that was clicked to display a content section.
     * @returns {void}
     */
    link_clicked(e: Event) {
        if (this.check_validity) {
            if (!(e.target as HTMLFormElement).form.checkValidity()) {
                return;
            }
        }

        let caller = (e.target || e.srcElement) as HTMLElement ;

        if (caller.tagName !== 'A') {
            caller = caller.querySelector('a');
        }

        const idx = parseInt(caller.getAttribute('data-idx'), 10);

        // Toggle content for mobile tab links.
        if (caller.classList.contains('tv-tablink__mobile')) {
            caller.classList.toggle('active');
            this.content[idx].style.display = 'none';
            if (idx === this.active_idx && caller.classList.contains('active')) {
                this.content[idx].style.display = 'block';
            }
        }

        if (idx !== this.active_idx) {
            this.set_active(idx);
        }
    }

    /**
     * Handles the selected link to display the corresponding content section based on direction.
     * Intended use would be for "Previous" and "Next" links.
     *
     * @param {Event} e The element that was clicked to display a content section.
     * @returns {void}
     */
    direction_button_clicked(e: Event) : void {
        if (this.check_validity) {
            if ((e.target as HTMLFormElement).form.checkValidity()) {
                return;
            }
        }

        const caller = (e.target || e.srcElement) as HTMLElement;

        const direction = caller.getAttribute('data-direction');

        if (direction === 'next') {
            this.set_active(this.active_idx + 1);
        }

        if (direction === 'previous') {
            this.set_active(this.active_idx - 1);
        }
    }

    /**
     * Get the active id link/section.
     *
     * @returns {number} The active index
     */
    get_active() {
        return this.active_idx;
    }

    /**
     * Set the active link/section based on the index.
     *
     * @param {number} idx The index of the section to set to be active
     * @returns {void}
     */
    set_active(idx: number) : void {
        // Update the UI styling.
        this.buttons.links[this.active_idx].classList.remove('active');
        this.buttons.links[idx].classList.add('active');

        this.buttons.mobile_links[this.active_idx].classList.remove('active');
        this.buttons.mobile_links[idx].classList.add('active');

        // Display the content based on the link.
        this.content[this.active_idx].style.display = 'none';
        this.content[idx].style.display = 'block';

        // Set the active index.
        this.active_idx = idx;

        // Resize modal for desktop tab content.
        if (!this.buttons.links[idx].classList.contains('tv-tablink__mobile')) {
            // @ts-ignore global variable $.
            $.ceDialog('get_last').ceDialog('reload');
        }

        // Let the caller know when the tab is changed.
        if (this.callback !== null) {
            this.callback(this.content[idx], idx);
        }

        // If there are direction buttons, set the display properties accordingly.
        if (this.buttons.direction_buttons) {
            for (const direction_button of this.buttons.direction_buttons as HTMLCollectionOf<HTMLElement>) {
                // Only one content element.
                if (this.content.length === 1) {
                    direction_button.style.display = 'none';
                // First link is active.
                } else if (this.active_idx === 0) {
                    if (direction_button.getAttribute('data-direction') === 'previous') {
                        direction_button.style.display = 'none';
                    } else {
                        direction_button.style.display = 'block';
                    }
                // Last link is active.
                } else if (this.active_idx === this.content.length - 1) {
                    if (direction_button.getAttribute('data-direction') === 'next') {
                        direction_button.style.display = 'none';
                    } else {
                        direction_button.style.display = 'block';
                    }
                // Anything between first and last.
                } else {
                    direction_button.style.display = 'block';
                }
            }
        }
    }
}